<template>
  <v-container fluid>
    <!-- 1) Header general: año, mes, semana, botón -->
    <v-row align="center" class="mb-4">
      <v-col cols="12" class="text-h5 font-weight-bold text-center">
        Calendario de Paros Programados de {{ nombreMes }} para {{ anioActual }}
      </v-col>
      <v-col cols="12">
        <div class="d-flex justify-center align-center">
          <v-autocomplete v-model="mesSeleccionado" :items="meses" label="Mes" dense outlined
            @change="actualizarSemanasDisponibles" hide-details :disabled="loadingCalendario" />
          <v-autocomplete v-model="semanaSeleccionada" :items="semanasDisponibles" label="Semana" dense outlined
            :disabled="!semanasDisponibles.length || loadingCalendario" @change="filtrarEventosPorSemana" hide-details
            class="mx-4" />
          <v-btn color="primary" @click="agregarEvento" :disabled="loadingCalendario">
            Programar Paros Planeados
          </v-btn>
          <!-- <v-btn color="primary" @click="abrirDialogMasivo" :disabled="loadingCalendario">MODIFICAR PAROS PLANEADOS</v-btn> -->
        </div>
      </v-col>
    </v-row>

    <!-- 2) Cabecera personalizada de días alineada -->
    <div class="calendar-header">
      <div v-for="(dia, i) in diasSemana" :key="i" class="day-header-cell">
        <div :class="['day-bubble', { today: dia.esHoy }]">
          {{ dia.numero }}
        </div>
        <div class="weekday-label">{{ dia.nombre }}</div>
      </div>
    </div>

    <v-sheet v-if="loadingCalendario" color="transparent" class="d-flex align-center justify-center" height="300">
      <v-progress-circular indeterminate size="56" width="4" color="primary" />
    </v-sheet>
    <!-- 3) Calendario “limpio” sin cabecera nativa -->
    <v-calendar v-else v-model="value" type="week" :weekdays="weekday" :events="eventosFiltrados" hide-header
      color="primary" :event-color="getEventColor" :event-text-color="() => '#ffffff'"
      @click:event="abrirDesdeCalendario">
      <template v-slot:event="{ event, timed }">
        <v-tooltip top open-delay="150" content-class="pp-tooltip" max-width="380" color="white" class="black--text">
          <template v-slot:activator="{ on, attrs }">
            <div class="my-event" :class="{ 'my-event--timed': timed }" v-bind="attrs" v-on="on">
              <div class="my-event__title">{{ event.title }}</div>
              <div v-if="event.subtitle" class="my-event__subtitle">{{ event.subtitle }}</div>
            </div>
          </template>

          <div class="tooltip-content">
            <div class="tooltip-row"><strong>Áreas:</strong> {{ areasLabel(event) }}</div>
            <div class="tooltip-row"><strong>Máquinas:</strong> {{ maquinasLabel(event) }}</div>
            <div v-if="event.descripcion" class="tooltip-row"><strong>Descripción:</strong> {{ event.descripcion }}
            </div>
          </div>
        </v-tooltip>
      </template>
    </v-calendar>

    <v-dialog v-model="dialog" max-width="650px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-center text-subtitle-2 font-weight-bold">
          {{ modoEdicion ? 'Editar paro programado' : 'Crear paro programado' }} ({{ anioActual }})
        </v-card-title>

        <!-- FORMULARIO -->
        <v-form ref="formParo" v-model="valido" lazy-validation @submit.prevent="onSubmit">
          <v-card-text>
            <!-- Causa y Subcausa -->
            <div class="mb-0">
              <v-row>
                <v-col cols="6">
                  <span class="text-subtitle-1 font-weight-bold text-center">Motivo</span>
                  <v-autocomplete v-model="form.motivo" label="Causa" :items="causas" item-text="nombre"
                    item-value="idParoTipo" outlined dense :rules="[v => !!v || 'Obligatorio']" required />
                </v-col>

                <v-col cols="6">
                  <span class="text-subtitle-1 font-weight-bold text-center">Submotivo (opcional)</span>
                  <v-autocomplete v-model="form.submotivo" label="Sub causa" :items="subcausasFiltradas"
                    item-text="nombreSubParoTipo" item-value="idSubParoTipo" outlined dense clearable />
                </v-col>
              </v-row>
            </div>

            <!-- Áreas y Máquinas -->
            <div class="mb-5">
              <v-row>
                <v-col cols="6">
                  <span class="text-subtitle-1 font-weight-bold text-center">Áreas</span>
                  <v-autocomplete v-model="form.areas" :items="areas" multiple label="Áreas" chips outlined dense
                    clearable :return-object="false"
                    :rules="[v => (Array.isArray(v) && v.length > 0) || 'Selecciona al menos una área']" required />
                </v-col>

                <v-col cols="6">
                  <span class="text-subtitle-1 font-weight-bold text-center">Máquinas</span>
                  <v-autocomplete v-model="form.maquinas" :items="maquinas" multiple label="Máquinas" chips outlined
                    dense clearable :return-object="false"
                    :rules="[v => (Array.isArray(v) && v.length > 0) || 'Selecciona al menos una máquina']" required />
                </v-col>
              </v-row>
            </div>

            <!-- Rango de fechas -->
            <span class="text-subtitle-1 font-weight-bold text-center">Rango de fechas</span>
            <div>
              <v-row>
                <v-col cols="6">
                  <v-menu v-model="menuDesde" :close-on-content-click="false" offset-y max-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="form.desde" label="Desde" readonly dense outlined v-bind="attrs" v-on="on"
                        :rules="[v => !!v || 'Obligatorio']" required />
                    </template>
                    <v-date-picker v-model="form.desde" :min="startOfYear" :max="form.hasta || endOfYear" locale="es"
                      color="primary" dark header-color="primary" reactive show-current @input="menuDesde = false" />
                  </v-menu>
                </v-col>

                <v-col cols="6">
                  <v-menu v-model="menuHasta" :close-on-content-click="false" offset-y max-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="form.hasta" label="Hasta" readonly dense outlined v-bind="attrs" v-on="on"
                        :rules="[v => !!v || 'Obligatorio']" required />
                    </template>
                    <v-date-picker v-model="form.hasta" :min="form.desde || startOfYear" :max="endOfYear" locale="es"
                      color="primary" header-color="primary" dark reactive show-current @input="menuHasta = false" />
                  </v-menu>
                </v-col>
              </v-row>
            </div>

            <!-- Días que aplica (requerido solo si hay más de 2) -->
            <div v-if="diasInvolucrados.length > 2">
              <span class="text-subtitle-1 font-weight-bold text-center">Días que aplica</span>

              <!-- Input “fantasma” para validar el array de días -->
              <v-input v-model="form.dias" :rules="[
                v => (diasInvolucrados.length <= 2) || (Array.isArray(v) && v.length > 0) || 'Selecciona al menos un día'
              ]" class="mt-1" hide-details="auto">
                <!-- <template v-slot:default>
                  <v-row class="my-2 mt-n3">
                    <v-col cols="auto" v-for="dia in diasInvolucrados" :key="dia">
                      <v-checkbox v-model="form.dias" :label="dia" :value="dia" dense hide-details />
                    </v-col>
                  </v-row>
                </template> -->
                <template v-slot:default>
                  <v-row class="my-2 mt-n3">
                    <v-col cols="auto" v-for="dia in diasSemanaEnRango" :key="dia.value">
                      <v-checkbox v-model="form.dias" :label="dia.label" :value="dia.value" dense hide-details />
                    </v-col>

                  </v-row>
                </template>
              </v-input>
            </div>

            <!-- Horarios -->
            <span class="text-subtitle-1 font-weight-bold text-center">Horarios</span>
            <v-col cols="12" class="mt-n3">
              <v-row class="mt-1">
                <v-menu v-model="menuInicio" :close-on-content-click="false" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.horaInicio" label="Hora Inicio" readonly v-bind="attrs" v-on="on"
                      outlined class="mr-3" :rules="[v => !!v || 'Obligatoria']" required />
                  </template>
                  <v-time-picker v-model="form.horaInicio" format="24hr" />
                </v-menu>

                <v-menu v-model="menuFin" :close-on-content-click="false" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.horaFin" label="Hora Fin" readonly v-bind="attrs" v-on="on" outlined
                      class="ml-3" :rules="[v => !!v || 'Obligatoria']" required />
                  </template>
                  <v-time-picker v-model="form.horaFin" format="24hr" />
                </v-menu>
              </v-row>
            </v-col>

            <!-- Alerta si cruza a día siguiente -->
            <v-alert v-if="esFinAntesQueInicio" color="orange lighten-4" dense elevation="0">
              <div class="d-flex justify-center align-center">
                <v-icon large class="mr-1" color="orange darken-1">mdi-alert-outline</v-icon>
                <span class="text-subtitle-1 font-weight-medium text-center">
                  La hora de fin es anterior a la de inicio; el paro continuará al día siguiente.
                </span>
              </div>
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-btn v-if="modoEdicion" color="error" @click="abrirEliminar">Eliminar</v-btn>
            <v-spacer />
            <v-btn text color="success" @click="cerrarDialog" :loading="loadingGuardar">Cancelar</v-btn>
            <v-btn text :color="modoEdicion ? 'error' : 'error'" :loading="loadingGuardar" type="submit">
              {{ modoEdicion ? 'Actualizar' : 'Guardar' }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dlgMasivo" max-width="1100px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-center text-subtitle-2 font-weight-bold">
          Modificar paros planeados
        </v-card-title>

        <v-card-text>
          <v-stepper v-model="stepMasivo" non-linear flat>
            <v-stepper-header>
              <v-stepper-step :complete="stepMasivo > 1" step="1" editable>Buscar</v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step step="2" :editable="seleccionMasiva.length > 0">
                Actualizar ({{ seleccionMasiva.length }})
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <!-- PASO 1 -->
              <v-stepper-content step="1">
                <v-form ref="formBusqueda" v-model="validoBusqueda" lazy-validation @submit.prevent="buscarParos">
                  <v-row>
                    <v-col cols="12" md="6">
                      <span class="text-subtitle-1 font-weight-bold">Rango de fechas</span>
                      <v-row>
                        <v-col cols="6">
                          <v-menu v-model="menuDesdeBusq" :close-on-content-click="false" offset-y max-width="290px">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field v-model="filtro.desde" label="Desde" readonly dense outlined v-bind="attrs"
                                v-on="on" />
                            </template>
                            <v-date-picker v-model="filtro.desde" locale="es" color="primary" header-color="primary"
                              dark reactive show-current @input="menuDesdeBusq = false" />
                          </v-menu>
                        </v-col>
                        <v-col cols="6">
                          <v-menu v-model="menuHastaBusq" :close-on-content-click="false" offset-y max-width="290px">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field v-model="filtro.hasta" label="Hasta" readonly dense outlined v-bind="attrs"
                                v-on="on" />
                            </template>
                            <v-date-picker v-model="filtro.hasta" locale="es" color="primary" header-color="primary"
                              dark reactive show-current @input="menuHastaBusq = false" />
                          </v-menu>
                        </v-col>
                        <!-- <v-col cols="6">
                          <v-menu v-model="menuHoraDesdeBusq" :close-on-content-click="false" offset-y>
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field v-model="filtro.horaDesde" label="Hora desde" readonly dense outlined
                                v-bind="attrs" v-on="on" />
                            </template>
                            <v-time-picker v-model="filtro.horaDesde" format="24hr" />
                          </v-menu>
                        </v-col>

                        <v-col cols="6">
                          <v-menu v-model="menuHoraHastaBusq" :close-on-content-click="false" offset-y>
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field v-model="filtro.horaHasta" label="Hora hasta" readonly dense outlined
                                v-bind="attrs" v-on="on" />
                            </template>
                            <v-time-picker v-model="filtro.horaHasta" format="24hr" />
                          </v-menu>
                        </v-col> -->
                      </v-row>
                    </v-col>

                    <v-col cols="12" md="6">
                      <span class="text-subtitle-1 font-weight-bold">Filtros</span>
                      <v-row>
                        <v-col cols="6">
                          <v-autocomplete v-model="filtro.IdArea" :items="areas" item-value="value" label="Área"
                            clearable dense outlined :loading="loadingAreas" />
                        </v-col>
                        <v-col cols="6">
                          <v-autocomplete v-model="filtro.IdMaquina" :items="maquinas" :item-text="nombreMaquina"
                            item-value="value" label="Máquina" clearable dense outlined :loading="loadingMaquinas" />
                        </v-col>
                        <v-col cols="6">
                          <v-autocomplete v-model="filtro.IdParoTipo" :items="causas" item-text="nombre"
                            item-value="idParoTipo" label="Motivo" clearable dense outlined :loading="loadingCausas" />
                        </v-col>
                        <v-col cols="6">
                          <v-autocomplete v-model="filtro.IdSubParoTipo" :items="subcausasFiltradas"
                            item-text="nombreSubParoTipo" item-value="idSubParoTipo" label="Submotivo" clearable dense
                            outlined :loading="loadingSubcausas" />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" class="d-flex justify-end">
                      <v-btn text @click="limpiarBusqueda" :disabled="loadingBusqueda">Limpiar</v-btn>
                      <v-btn color="primary" :loading="loadingBusqueda" type="submit">Buscar</v-btn>
                    </v-col>
                  </v-row>
                </v-form>

                <v-data-table v-model="seleccionMasiva" :headers="headersParos" :items="resultadosParos"
                  item-key="idParoProgramado" show-select :loading="loadingBusqueda">
                  <template v-slot:item.desde="{ item }">
                    {{ dateOnly(item.desde) }}
                  </template>

                  <template v-slot:item.hasta="{ item }">
                    {{ dateOnly(item.hasta) }}
                  </template>

                  <template v-slot:item.areasDetalle="{ item }">
                    {{(item.areasDetalle || []).map(a => a.nombre).join(', ')}}
                  </template>

                  <template v-slot:item.maquinasDetalle="{ item }">
                    {{(item.maquinasDetalle || []).map(m => m.nombre).join(', ')}}
                  </template>
                  <!-- <template v-slot:item.areas="{ item }">
                    <v-chip v-for="a in (item.areas || [])" :key="a.id || a" small class="ma-1">{{ a.nombre || a
                    }}</v-chip>
                  </template>
                  <template v-slot:item.maquinas="{ item }">
                    <v-chip v-for="m in (item.maquinas || [])" :key="m.id || m" small class="ma-1">{{ m.nombre || m
                    }}</v-chip>
                  </template> -->
                </v-data-table>

                <v-row>
                  <v-col cols="12" class="d-flex justify-end">
                    <v-btn color="primary" :disabled="seleccionMasiva.length === 0" @click="stepMasivo = 2">
                      Siguiente ({{ seleccionMasiva.length }})
                    </v-btn>
                  </v-col>
                </v-row>
              </v-stepper-content>

              <!-- PASO 2 -->
              <v-stepper-content step="2">
                <v-alert type="info" dense>
                  Se actualizarán {{ seleccionMasiva.length }} paros.
                </v-alert>

                <v-form ref="formMasivo" v-model="validoMasivo" lazy-validation @submit.prevent="aplicarMasivo">
                  <v-card-text>
                    <!-- Causa y Subcausa (idéntico al de crear/editar) -->
                    <div class="mb-0">
                      <v-row>
                        <v-col cols="6">
                          <span class="text-subtitle-1 font-weight-bold text-center">Motivo</span>
                          <v-autocomplete v-model="form.motivo" label="Causa" :items="causas" item-text="nombre"
                            item-value="idParoTipo" outlined dense :rules="[v => !!v || 'Obligatorio']" required />
                        </v-col>

                        <v-col cols="6">
                          <span class="text-subtitle-1 font-weight-bold text-center">Submotivo (opcional)</span>
                          <v-autocomplete v-model="form.submotivo" label="Sub causa" :items="subcausasFiltradas"
                            item-text="nombreSubParoTipo" item-value="idSubParoTipo" outlined dense clearable />
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Áreas y Máquinas -->
                    <div class="mb-5">
                      <v-row>
                        <v-col cols="6">
                          <span class="text-subtitle-1 font-weight-bold text-center">Áreas</span>
                          <v-autocomplete v-model="form.areas" :items="areas" multiple label="Áreas" chips outlined
                            dense clearable :return-object="false"
                            :rules="[v => (Array.isArray(v) && v.length > 0) || 'Selecciona al menos una área']"
                            required />
                        </v-col>

                        <v-col cols="6">
                          <span class="text-subtitle-1 font-weight-bold text-center">Máquinas</span>
                          <v-autocomplete v-model="form.maquinas" :items="maquinas" multiple label="Máquinas" chips
                            outlined dense clearable :return-object="false"
                            :rules="[v => (Array.isArray(v) && v.length > 0) || 'Selecciona al menos una máquina']"
                            required />
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Rango de fechas -->
                    <span class="text-subtitle-1 font-weight-bold text-center">Rango de fechas</span>
                    <div>
                      <v-row>
                        <v-col cols="6">
                          <v-menu v-model="menuDesde1" :close-on-content-click="false" offset-y max-width="290px">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field v-model="form.desde" label="Desde" readonly dense outlined v-bind="attrs"
                                v-on="on" :rules="[v => !!v || 'Obligatorio']" required />
                            </template>
                            <v-date-picker v-model="form.desde" :min="startOfYear" :max="form.hasta || endOfYear"
                              locale="es" color="primary" dark header-color="primary" reactive show-current
                              @input="menuDesde1 = false" />
                          </v-menu>
                        </v-col>

                        <v-col cols="6">
                          <v-menu v-model="menuHasta1" :close-on-content-click="false" offset-y max-width="290px">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field v-model="form.hasta" label="Hasta" readonly dense outlined v-bind="attrs"
                                v-on="on" :rules="[v => !!v || 'Obligatorio']" required />
                            </template>
                            <v-date-picker v-model="form.hasta" :min="form.desde || startOfYear" :max="endOfYear"
                              locale="es" color="primary" dark header-color="primary" reactive show-current
                              @input="menuHasta1 = false" />
                          </v-menu>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Días que aplica (requerido solo si hay más de 2) -->
                    <div v-if="diasInvolucrados.length > 2">
                      <span class="text-subtitle-1 font-weight-bold text-center">Días que aplica</span>

                      <!-- Input “fantasma” para validar el array de días -->
                      <v-input v-model="form.dias" :rules="[
                        v => (diasInvolucrados.length <= 2) || (Array.isArray(v) && v.length > 0) || 'Selecciona al menos un día'
                      ]" class="mt-1" hide-details="auto">
                        <template v-slot:default>
                          <v-row class="my-2 mt-n3">
                            <v-col cols="auto" v-for="dia in diasSemanaEnRango" :key="dia.value">
                              <v-checkbox v-model="form.dias" :label="dia.label" :value="dia.value" dense
                                hide-details />
                            </v-col>
                          </v-row>
                        </template>
                      </v-input>
                    </div>

                    <!-- Horarios -->
                    <span class="text-subtitle-1 font-weight-bold text-center">Horarios</span>
                    <v-col cols="12" class="mt-n3">
                      <v-row class="mt-1">
                        <v-menu v-model="menuInicio" :close-on-content-click="false" offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="form.horaInicio" label="Hora Inicio" readonly v-bind="attrs"
                              v-on="on" outlined class="mr-3" :rules="[v => !!v || 'Obligatoria']" required />
                          </template>
                          <v-time-picker v-model="form.horaInicio" format="24hr" />
                        </v-menu>

                        <v-menu v-model="menuFin" :close-on-content-click="false" offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="form.horaFin" label="Hora Fin" readonly v-bind="attrs" v-on="on"
                              outlined class="ml-3" :rules="[v => !!v || 'Obligatoria']" required />
                          </template>
                          <v-time-picker v-model="form.horaFin" format="24hr" />
                        </v-menu>
                      </v-row>
                    </v-col>

                    <!-- Alerta si cruza a día siguiente -->
                    <v-alert v-if="esFinAntesQueInicio" color="orange lighten-4" dense elevation="0">
                      <div class="d-flex justify-center align-center">
                        <v-icon large class="mr-1" color="orange darken-1">mdi-alert-outline</v-icon>
                        <span class="text-subtitle-1 font-weight-medium text-center">
                          La hora de fin es anterior a la de inicio; el paro continuará al día siguiente.
                        </span>
                      </div>
                    </v-alert>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="stepMasivo = 1" :disabled="loadingActualizar">Volver</v-btn>
                    <v-btn color="primary" :loading="loadingActualizar" type="submit">Aplicar cambios</v-btn>
                  </v-card-actions>
                </v-form>
              </v-stepper-content>

            </v-stepper-items>
          </v-stepper>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <!-- <v-btn text @click="stepMasivo = 1" :disabled="loadingActualizar">Volver</v-btn> -->
          <!-- <v-btn color="primary" :loading="loadingActualizar" type="submit">Aplicar cambios</v-btn> -->
          <v-btn @click="dlgMasivo = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogEliminar" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">Confirmar eliminación</v-card-title>

        <v-card-text>
          <!-- <v-alert type="error" dense prominent>
            Esta acción <strong>no es reversible</strong>.
          </v-alert> -->

          <!-- Datos SOLO texto (sin fechas) -->
          <div class="subtitle-2 mt-2"><strong>Causa:</strong> {{ textoCausa }}</div>
          <div class="subtitle-2 mt-1"><strong>Subcausa:</strong> {{ textoSubcausa }}</div>
          <div class="subtitle-2 mt-2"><strong>Áreas:</strong> {{ textoAreas }}</div>
          <div class="subtitle-2 mt-1"><strong>Máquinas:</strong> {{ textoMaquinas }}</div>

          <!-- <v-alert v-if="eliminarError" type="error" dense class="mt-4">
            {{ eliminarError }}
          </v-alert> -->
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text color="success" @click="dialogEliminar = false" :disabled="eliminarLoading">Cancelar</v-btn>
          <v-btn color="orange darken-2" text dark :loading="eliminarLoading" @click="eliminarActual">
            Eliminar actual
          </v-btn>
          <v-btn color="error" text dark :loading="eliminarLoading" @click="eliminarTodos">
            Eliminar todos
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <!-- <v-dialog v-model="dialog">

    </v-dialog> -->
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    const year = new Date().getFullYear();
    const hoy = new Date();
    return {
      value: hoy.toISOString().substr(0, 10),
      anioActual: hoy.getFullYear(),

      // rangos año (para <v-date-picker :min/:max>)
      startOfYear: `${year}-01-01`,
      endOfYear: `${year}-12-31`,

      mesSeleccionado: null,
      semanaSeleccionada: null,
      semanasDisponibles: [],

      dialog: false,
      weekday: [1, 2, 3, 4, 5, 6, 0],

      eventosJSON: [
        {
          titulo: "Paro Por Cambio De Turno",
          descripcion: "Este paro se realiza cuando el personal realiza el cambio de turno",
          horaInicio: "10:00",
          horaFin: "15:00",
          desde: "2025-07-31",
          hasta: "2025-07-31",
          areas: [1, 2, 3, 4],
          maquinas: [1, 2, 3, 4],
        }
      ],
      eventosFiltrados: [],
      diasSemana: [],

      meses: [
        { text: 'Enero', value: 0 }, { text: 'Febrero', value: 1 }, { text: 'Marzo', value: 2 },
        { text: 'Abril', value: 3 }, { text: 'Mayo', value: 4 }, { text: 'Junio', value: 5 },
        { text: 'Julio', value: 6 }, { text: 'Agosto', value: 7 }, { text: 'Septiembre', value: 8 },
        { text: 'Octubre', value: 9 }, { text: 'Noviembre', value: 10 }, { text: 'Diciembre', value: 11 }
      ],

      // menús form crear/editar (se reutilizan en Paso 2)
      menuInicio: false,
      menuFin: false,
      menuDesde: false,
      menuHasta: false,
      menuDesde1: false,
      menuHasta1: false,

      // form único (se reutiliza en Paso 2)
      form: {
        motivo: null,
        titulo: null,
        submotivo: null,
        descripcion: null,
        dias: [],
        horaInicio: null,
        horaFin: null,
        desde: null,
        hasta: null,
        areas: [],
        maquinas: [],
      },

      diasDisponibles: [
        { label: 'Lunes', value: 1 },
        { label: 'Martes', value: 2 },
        { label: 'Miércoles', value: 3 },
        { label: 'Jueves', value: 4 },
        { label: 'Viernes', value: 5 },
        { label: 'Sábado', value: 6 },
        { label: 'Domingo', value: 0 },
      ],

      // (si usas estas fechas en otro lado, las conservo)
      minDate: new Date(year, 0, 3),
      maxDate: new Date(year, 11, 31),

      // cargas
      loadingAreas: false,
      loadingMaquinas: false,
      loadingCausas: false,
      loadingSubcausas: false,
      loadingCalendario: false,

      // catálogos
      areas: [],
      maquinas: [],
      causas: [],
      subcausas: [],
      subcausasFiltradas: [],

      // estado form crear/editar
      valido: false,
      loadingGuardar: false,
      modoEdicion: false,
      editandoId: null,

      // diálogo masivo
      dlgMasivo: false,
      stepMasivo: 1,

      // búsqueda
      validoBusqueda: false,
      loadingBusqueda: false,
      filtro: {
        desde: null,
        horaDesde: null,
        hasta: null,
        horaHasta: null,
        IdArea: null,
        IdMaquina: null,
        IdParoTipo: null,
        IdSubParoTipo: null
      },
      menuDesdeBusq: false,
      menuHastaBusq: false,
      menuHoraDesdeBusq: false,
      menuHoraHastaBusq: false,

      // resultados
      resultadosParos: [],
      headersParos: [
        { text: 'Causa', value: 'motivoNombre' },
        { text: 'Sub Causa', value: 'submotivoNombre' },
        { text: 'Inicio', value: 'desde' },
        { text: 'Hora Inicio', value: 'horaInicio' },
        { text: 'Fin', value: 'hasta' },
        { text: 'Hora Fin', value: 'horaFin' },
        { text: 'Áreas', value: 'areasDetalle', sortable: false },
        { text: 'Máquinas', value: 'maquinasDetalle', sortable: false },
      ],
      seleccionMasiva: [],

      // actualización masiva
      validoMasivo: false,
      loadingActualizar: false,
      originalMasivo: null, // snapshot para detectar cambios en Paso 2

      dialogEliminar: false,
      eliminarValid: true,
      eliminarLoading: false,
      eliminarError: '',
      eliminarForm: {
        idParoProgramado: null,
        desde: null,     // "YYYY-MM-DD"
        hasta: null,     // "YYYY-MM-DD"
        areas: [],       // [ids]
        maquinas: [],    // [ids]
        motivo: ''       // opcional
      },
      // regla común (usa la tuya si ya existe)
      req: v => !!v || 'Requerido',
      // Catálogos (usa los tuyos si ya existen)
      areasOptions: [],
      maquinasOptions: [],
    };
  },

  mounted() {
    this.initialize();
    // 1) Selecciona el mes de hoy
    const hoy = new Date();
    this.mesSeleccionado = hoy.getMonth();

    // 2) Genera las semanas para ese mes
    this.actualizarSemanasDisponibles();

    // 3) Busca la semana que contenga hoy
    const hoyTime = hoy.getTime();
    const encontrada = this.semanasDisponibles.find(s => {
      const inicio = new Date(s.value).getTime();
      const fin = new Date(s.value);
      fin.setDate(fin.getDate() + 6);
      return hoyTime >= inicio && hoyTime <= fin.getTime();
    });
    this.semanaSeleccionada = encontrada
      ? encontrada.value
      : this.semanasDisponibles[0]?.value;

    // 4) Finalmente filtra eventos y construye el header
    this.filtrarEventosPorSemana();
  },
  methods: {
    initialize() {
      this.getAreas();
      this.getMaquinas();
      this.getCausas();
      this.getSubcausas();
    },
    getAreas() {
      this.loadingAreas = true;
      axios.get('/Areas')
        .then(({ data }) => {
          // Ajusta los nombres reales que te devuelve el backend:
          // si viene Nombre/IdArea en mayúsculas, queda así.
          this.areas = (data || []).map(a => ({
            text: a.Nombre ?? a.nombre ?? String(a.text ?? a.Nombre ?? a.nombre),
            value: Number(a.IdArea ?? a.idArea ?? a.value)
          }));
        })
        .catch(console.error)
        .finally(() => { this.loadingAreas = false; });
    },
    getMaquinas() {
      this.loadingMaquinas = true;
      axios.get('/MaquinaInstancias')
        .then(({ data }) => {
          this.maquinas = (data || []).map(m => ({
            text: m.Nombre ?? m.nombre ?? String(m.text ?? m.Nombre ?? m.nombre),
            value: Number(m.IdMaquinaIns ?? m.idMaquinaIns ?? m.value)
          }));
        })
        .catch(console.error)
        .finally(() => { this.loadingMaquinas = false; });
    },
    getCausas() {
      this.loadingCausas = true;
      axios
        .get("/ParoTipos")
        .then(response => this.causas = response.data.filter(item => item.oeeFactor === "DISPONIBILIDAD" && item.paroProgramado))
        .catch(error => console.log(error))
        .finally(() => { this.loadingCausas = false; })
    },
    getSubcausas() {
      this.loadingSubcausas = true;
      axios
        .get("/SubParoTipos")
        .then(response => this.subcausas = response.data)
        .catch(error => console.log(error))
        .finally(() => { this.loadingSubcausas = false; })
    },

    abrirCrearParo(ymd = null) {
      this.resetForm();
      this.modoEdicion = false;
      this.editandoId = null;
      if (ymd) this.form.desde = this.form.hasta = ymd;
      this.dialog = true;
    },
    // Endpoints (ajusta si tus rutas cambian)
    crearParo(payload) {
      return axios.post('/ParoProgramado/Crear', payload);
    },
    actualizarParo(payload) {
      return axios.put(`/ParoProgramado/ActualizarMasivo`, payload);
    },

    // Reset suave (útil para abrir en modo crear)
    resetForm() {
      this.menuDesde = this.menuHasta = this.menuDesde1 = this.menuHasta1 = this.menuInicio = this.menuFin = false;
      this.form = {
        motivo: null, titulo: null, submotivo: null, descripcion: null,
        dias: [], horaInicio: null, horaFin: null,
        desde: null, hasta: null, areas: [], maquinas: []
      };
      this.subcausasFiltradas = [];
      this.valido = false;
      this.$nextTick(() => this.$refs.formParo?.resetValidation());
    },

    agregarEvento() { this.abrirCrearParo(); },
    getEventColor(evt) {
      return evt.color || 'blue';
    },
    actualizarSemanasDisponibles() {
      const s = [];
      const inicioMes = new Date(this.anioActual, this.mesSeleccionado, 1);
      const finMes = new Date(this.anioActual, this.mesSeleccionado + 1, 0);
      let cur = new Date(inicioMes);
      while (cur <= finMes) {
        const ini = new Date(cur);
        const fin = new Date(cur); fin.setDate(fin.getDate() + 6);
        s.push({
          text: `${ini.toLocaleDateString()} - ${fin.toLocaleDateString()}`,
          value: new Date(ini)
        });
        cur.setDate(cur.getDate() + 7);
      }
      this.semanasDisponibles = s;
      this.semanaSeleccionada = s[0]?.value || null;
      this.filtrarEventosPorSemana();
    },

    mapDiaNombreANum(nombre) {
      const mapa = {
        'Lunes': 1, 'Martes': 2, 'Miércoles': 3, 'Miercoles': 3,
        'Jueves': 4, 'Viernes': 5, 'Sábado': 6, 'Sabado': 6, 'Domingo': 7
      };
      return mapa[nombre];
    },
    toISO(d) { return new Date(d).toISOString().slice(0, 10); },
    toLocalYMD(d) {
      const x = new Date(d);
      const y = x.getFullYear();
      const m = String(x.getMonth() + 1).padStart(2, '0');
      const day = String(x.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    startOfWeek(d, first = 0) {
      const x = new Date(d);
      const day = x.getDay();                 // 0..6 (dom..sáb)
      const diff = (day - first + 7) % 7;
      x.setDate(x.getDate() - diff);
      x.setHours(0, 0, 0, 0);
      return x;
    },
    areasLabel(evt) {
      const arr = evt.areas || [];
      const names = arr.map(a => a.nombre || a.name || a.text || a).filter(Boolean);
      return names.length ? names.join(', ') : '—';
    },
    maquinasLabel(evt) {
      const arr = evt.maquinas || [];
      const names = arr.map(m => m.nombre || m.name || m.text || m).filter(Boolean);
      return names.length ? names.join(', ') : '—';
    },
    seededColor(key) {
      const s = String(key ?? Math.random());
      let h = 2166136261;
      for (let i = 0; i < s.length; i++) h = (h ^ s.charCodeAt(i)) * 16777619 >>> 0;

      const hue = h % 360;
      const sat = 70;  // %
      const lig = 42;  // %
      return `hsl(${hue}, ${sat}%, ${lig}%)`;
    },

    async filtrarEventosPorSemana() {
      if (!this.semanaSeleccionada) return;
      this.loadingCalendario = true;

      // const segInicio = new Date(this.semanaSeleccionada);
      // const segFin = new Date(segInicio); segFin.setDate(segFin.getDate() + 6);
      const segInicio = new Date(
        typeof this.semanaSeleccionada === 'string' && this.semanaSeleccionada.length === 10
          ? `${this.semanaSeleccionada}T00:00:00`
          : this.semanaSeleccionada
      );

      const segFin = new Date(segInicio); segFin.setDate(segFin.getDate() + 6);


      // Alinear encabezado y vista a la semana elegida
      const startWd = segInicio.getDay();
      this.weekday = Array.from({ length: 7 }, (_, i) => (startWd + i) % 7);

      try {
        const { data } = await axios.get('/ParoProgramado/Listar',
          {
            params: {
              desde: this.toLocalYMD(segInicio),
              hasta: this.toLocalYMD(segFin),
              incluirInactivos: false
            }
          }
        );

        const items = Array.isArray(data) ? data : [];
        const eventos = [];

        items.forEach(it => {
          // Fechas del programa (LOCAL)
          const ymdIni = (it.desde || '').slice(0, 10);
          const ymdFin = (it.hasta || '').slice(0, 10);
          const progIni = ymdIni ? new Date(`${ymdIni}T00:00:00`) : new Date(it.desde);
          const progFin = ymdFin ? new Date(`${ymdFin}T00:00:00`) : new Date(it.hasta);

          // Si no toca la semana, salta
          if (progFin < segInicio || progIni > segFin) return;

          // Intersección con la semana
          const dIni = new Date(Math.max(segInicio.getTime(), progIni.getTime()));
          const dFin = new Date(Math.min(segFin.getTime(),    progFin.getTime()));

          // Normaliza días del back: 1..7 -> 0..6 (7=domingo->0)
          const diasJS = (Array.isArray(it.dias) ? it.dias : [])
            .map(n => Number(n))
            .filter(n => !Number.isNaN(n))
            .map(n => (n === 7 ? 0 : (n % 7)));

          for (let d = new Date(dIni); d <= dFin; d.setDate(d.getDate() + 1)) {
            const esDomingo = d.getDay() === 0;
            if (esDomingo) console.log('Domingo candidato:', {
              fecha: d.toDateString(), diasBack: it.dias, diasJS
            });

            // ✅ REACTIVA el filtro por días
            if (diasJS.length && !diasJS.includes(d.getDay())) {
              if (esDomingo) console.log('Domingo descartado por diasJS:', { diasJS });
              continue;
            }

            const ymd = this.toLocalYMD(d);
            const hi = (it.horaInicio && it.horaInicio.length >= 4) ? it.horaInicio.slice(0,5) : '00:00';
            const hf = (it.horaFin    && it.horaFin.length    >= 4) ? it.horaFin.slice(0,5)    : '23:59';

            const [hih, him] = hi.split(':').map(Number);
            const [hfh, hfm] = hf.split(':').map(Number);

            const startDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hih, him, 0, 0);
            let   endDate   = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hfh, hfm, 0, 0);
            if ((hfh*60 + hfm) <= (hih*60 + him)) endDate.setDate(endDate.getDate() + 1); // cruza día

            if (esDomingo) console.log('Domingo agregado:', { ymd, hi, hf });

            eventos.push({
              title: it.motivoNombre || 'Paro programado',
              subtitle: it.submotivoNombre || null,
              descripcion: it.descripcion || null,
              areas: it.areasDetalle || [],
              maquinas: it.maquinasDetalle || [],
              start: startDate,
              end: endDate,
              color: this.seededColor(`${it.idParoProgramado}-${ymd}`),
              timed: true,
              idParoProgramado: it.idParoProgramado,
              idParo: it.idParoProgramado,
              idParoTipo: it.idParoTipo ?? null,
              idSubParoTipo: it.idSubParoTipo ?? null,
              idProgramado: it.idProgramado ?? null,
              activo: it.activo ?? true
            });
          }
        });


        this.eventosFiltrados = eventos;
        this.value = this.toLocalYMD(segInicio);

        // Header de días
        const weekStart = new Date(segInicio);
        const hoyStr = new Date().toDateString();
        this.diasSemana = Array.from({ length: 7 }, (_, i) => {
          const d = new Date(weekStart); d.setDate(d.getDate() + i);
          return {
            numero: d.getDate(),
            nombre: d.toLocaleDateString('es-MX', { weekday: 'short' }).toUpperCase(),
            esHoy: d.toDateString() === hoyStr
          };
        });
      } catch (err) {
        console.error('Calendario error:', err.response?.status, err.response?.data || err.message);
        this.eventosFiltrados = [];
      } finally {
        this.loadingCalendario = false;
      }
    },
    cerrarDialog() {
      // Cerrar pickers/menús abiertos
      this.menuDesde = false;
      this.menuDesde1 = false;
      this.menuHasta = false;
      this.menuHasta1 = false;
      this.menuInicio = false;
      this.menuFin = false;

      // Limpiar validaciones del <v-form ref="formParo">
      if (this.$refs.formParo) {
        this.$refs.formParo.resetValidation();
      }

      // Reset explícito del modelo
      this.form = {
        titulo: null,
        descripcion: null,
        motivo: null,
        submotivo: null,   // opcional
        areas: [],
        maquinas: [],
        desde: null,
        hasta: null,
        dias: [],
        horaInicio: null,
        horaFin: null
      };

      // Auxiliares/flags
      this.subcausasFiltradas = [];
      this.valido = false;

      // Cerrar diálogo
      this.dialog = false;
      this.modoEdicion = false;
      this.editandoId = null;
    },
    guardarParo() { return this.onSubmit(); },

    // Abre el diálogo desde un evento del calendario y carga los datos para editar
    abrirDesdeCalendario(payload) {
      const ev = payload?.event ?? payload;
      if (!ev) return;

      const sStart = ev.start || ev.inicio || ev.fechaInicio || null;
      const sEnd = ev.end || ev.fin || ev.fechaFin || null;
      const startDT = this.parseCalDT(sStart);
      const endDT = this.parseCalDT(sEnd);

      this.form = {
        titulo: ev.title || ev.titulo || ev.name || null,
        descripcion: ev.descripcion || ev.description || null,
        motivo: ev.idParoTipo ?? null,        // requerido
        submotivo: ev.idSubParoTipo ?? null,     // opcional
        areas: Array.isArray(ev.areas) ? ev.areas.map(a => a?.id ?? a) : [],
        maquinas: Array.isArray(ev.maquinas) ? ev.maquinas.map(m => m?.id ?? m) : [],
        desde: this.extractYMD(sStart) || this.toYMD(startDT),
        hasta: this.extractYMD(sEnd) || this.toYMD(endDT),
        horaInicio: this.extractHM(sStart) || this.toHM(startDT),
        horaFin: this.extractHM(sEnd) || this.toHM(endDT),
        dias: [],
        idProgramado: ev.idProgramado,
        idParoProgramado: ev.idParoProgramado,
      };

      // this.recalcularDiasInvolucrados();
      // if (this.diasInvolucrados.length > 2) this.form.dias = [...this.diasInvolucrados];

      this.modoEdicion = true;
      this.editandoId = ev.idParo ?? ev.id ?? null;

      this.$nextTick(() => this.$refs.formParo?.resetValidation());
      this.dialog = true;
      this.menuDesde = this.menuHasta = this.menuInicio = this.menuFin = false;
    },
    async onSubmit() {
      const ok = this.$refs.formParo?.validate();
      if (!ok) return;

      const payload = this.armarPayload();
      this.loadingGuardar = true;
      try {
        if (this.modoEdicion && this.editandoId) {
          await this.actualizarParo(payload);
        } else {
          await this.crearParo(payload);
        }
        this.cerrarDialog();
        this.filtrarEventosPorSemana?.();
      } finally {
        this.loadingGuardar = false;
      }
    },

    armarPayload() {
      // 0..6 permitidos en la vista; si no existe, admite todos
      const permitidos = new Set(
        (this.diasSemanaEnRango?.map(d => Number(d.value))) ?? [0,1,2,3,4,5,6]
      );

      // Días auto (0..6) del rango [desde..hasta] inclusive, en LOCAL
      const autoDias0a6 = (() => {
        const ds = new Date(`${this.form.desde}T00:00:00`);
        const hs = new Date(`${this.form.hasta}T00:00:00`);
        if (isNaN(ds) || isNaN(hs) || hs < ds) return [];
        const s = new Set();
        const d = new Date(ds);
        while (d <= hs) { s.add(d.getDay()); d.setDate(d.getDate() + 1); }
        return Array.from(s);
      })();

      // Unir con UI (0..6), filtrar por permitidos
      const diasUI0a6 = Array.isArray(this.form.dias) ? this.form.dias.map(Number) : [];
      let dias0a6 = [...new Set([...autoDias0a6, ...diasUI0a6])]
        .filter(v => permitidos.has(v))
        .sort((a, b) => a - b);

      // Fallback: si quedó vacío, usar el día de 'desde'
      if (dias0a6.length === 0 && this.form?.desde) {
        dias0a6 = [new Date(`${this.form.desde}T00:00:00`).getDay()];
      }

      // Convertir a formato del back: 1..7 (domingo=7)
      const dias1a7 = [...new Set(dias0a6.map(d => (d === 0 ? 7 : d)))].sort((a,b) => a - b);

      return {
        titulo: this.form.titulo?.trim() || null,
        descripcion: this.form.descripcion?.trim() || null,
        idParoTipo: this.form.motivo,
        idSubParoTipo: this.form.submotivo || null,
        areas: Array.isArray(this.form.areas) ? this.form.areas : [],       // IDs
        maquinas: Array.isArray(this.form.maquinas) ? this.form.maquinas : [], // IDs
        desde: this.form.desde,            // "YYYY-MM-DD"
        hasta: this.form.hasta,            // "YYYY-MM-DD"
        horaInicio: (this.form.horaInicio || '').slice(0,5), // "HH:mm"
        horaFin:   (this.form.horaFin   || '').slice(0,5),   // "HH:mm"
        dias: dias1a7,                     // ← BACK: 1..7 (7=domingo)
        reasignarIdProgramado: false,
        idParoProgramado: this.form.idParoProgramado ?? null,
        idProgramadoOrigen: this.form.idProgramado
      };
    },


    // Helpers
    // parseCalDT(s) {
    //   if (!s) return null;
    //   // const [d, t = '00:00'] = String(s).split(' ');
    //   const [d, t = '00:00'] = String(s).split(/[ T]/);
    //   const [Y, M, D] = d.split('-').map(Number);
    //   const [h, m] = t.split(':').map(Number);
    //   const dt = new Date(Y, (M || 1) - 1, D || 1, h || 0, m || 0, 0, 0);
    //   return isNaN(dt.getTime()) ? null : dt;
    // },
    // toYMD(d) { if (!d) return null; const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0'); return `${y}-${m}-${day}`; },
    // toHM(d) { if (!d) return null; const h = String(d.getHours()).padStart(2, '0'), m = String(d.getMinutes()).padStart(2, '0'); return `${h}:${m}`; },
    // recalcularDiasInvolucrados() {
    //   const { desde, hasta } = this.form;
    //   const out = [];
    //   if (!desde || !hasta) { this.diasInvolucrados = []; return; }
    //   let cur = new Date(`${desde}T00:00:00`);
    //   const fin = new Date(`${hasta}T00:00:00`);
    //   while (cur <= fin) { out.push(this.toYMD(cur)); cur.setDate(cur.getDate() + 1); }
    //   this.diasInvolucrados = out;
    // },
    // ---------- Helpers mínimos ----------
    parseCalDT(s) {
      if (!s) return null;
      if (s instanceof Date) return new Date(s);
      const [d, t = '00:00'] = String(s).split(/[ T]/);
      const [Y, M, D] = d.split('-').map(Number);
      const [h, m] = t.split(':').map(Number);
      const dt = new Date(Y, (M || 1) - 1, D || 1, h || 0, m || 0);
      return isNaN(dt.getTime()) ? null : dt;
    },
    extractYMD(s) { const m = String(s || '').match(/^(\d{4}-\d{2}-\d{2})/); return m ? m[1] : null; },
    extractHM(s) { const m = String(s || '').match(/[ T](\d{2}):(\d{2})/); return m ? `${m[1]}:${m[2]}` : null; },
    toYMD(d) { if (!d) return null; const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0'); return `${y}-${m}-${day}`; },
    toHM(d) { if (!d) return null; const h = String(d.getHours()).padStart(2, '0'), m = String(d.getMinutes()).padStart(2, '0'); return `${h}:${m}`; },


    abrirDialogMasivo() {
      this.stepMasivo = 1;
      this.dlgMasivo = true;
      this.seleccionMasiva = [];
    },

    cerrarMasivo() {
      this.dlgMasivo = false;
      this.seleccionMasiva = [];
      this.resultadosParos = [];
      this.$refs.formBusqueda?.resetValidation();
      this.$refs.formMasivo?.resetValidation();
    },


    toDateTimeParam(d, hm) {
      if (!d) return null;
      const hhmm = (hm && /^\d{2}:\d{2}$/.test(hm)) ? hm : '00:00';
      return `${d} ${hhmm}`;
    },

    // Agrega si no la tienes
    dateOnly(s) { return s ? String(s).slice(0, 10) : null; },

    // Reemplaza tu método por este
    async buscarParos() {
      this.loadingBusqueda = true;
      try {
        const paramsRaw = {
          desde: this.dateOnly(this.filtro.desde),   // "YYYY-MM-DD"
          hasta: this.dateOnly(this.filtro.hasta),   // "YYYY-MM-DD"
          IdArea: this.filtro.IdArea != null ? this.filtro.IdArea : null,
          IdMaquina: this.filtro.IdMaquina != null ? this.filtro.IdMaquina : null,
          IdParoTipo: this.filtro.IdParoTipo != null ? this.filtro.IdParoTipo : null,
          IdSubParoTipo: this.filtro.IdSubParoTipo != null ? this.filtro.IdSubParoTipo : null
        };

        const params = Object.fromEntries(
          Object.entries(paramsRaw).filter(([, v]) => v !== null && v !== undefined && v !== '')
        );

        const { data } = await axios.get('/ParoProgramado/Listar', { params });
        this.resultadosParos = Array.isArray(data) ? data : [];

        // ← mapea al nuevo shape del EP
        // this.resultadosParos = (data || []).map(e => ({
        //   idParo: e.idParoProgramado,
        //   title: e.motivoNombre ?? 'Paro programado',
        //   descripcion: e.descripcion ?? null,
        //   start: `${String(e.desde).slice(0, 10)}T${(e.horaInicio || '00:00').slice(0, 5)}`,
        //   end: `${String(e.hasta).slice(0, 10)}T${(e.horaFin || '00:00').slice(0, 5)}`,
        //   areas: Array.isArray(e.areas) && e.areas.length
        //     ? e.areas
        //     : (Array.isArray(e.areasDetalle) ? e.areasDetalle.map(a => a.id) : []),
        //   maquinas: Array.isArray(e.maquinas) && e.maquinas.length
        //     ? e.maquinas
        //     : (Array.isArray(e.maquinasDetalle) ? e.maquinasDetalle.map(m => m.id) : []),
        //   idParoTipo: e.idParoTipo ?? null,
        //   idSubParoTipo: e.idSubParoTipo ?? null,
        //   dias: Array.isArray(e.dias) ? e.dias : []
        // }));

        this.seleccionMasiva = [];
        this.stepMasivo = 1;
      } catch (err) {
        console.error('Buscar paros error:', err.response?.data || err.message);
        this.resultadosParos = [];
        this.seleccionMasiva = [];
      } finally {
        this.loadingBusqueda = false;
      }
    },


    async aplicarMasivo() {
      if (this.seleccionMasiva.length === 0) return;

      const payload = {
        ids: this.seleccionMasiva.map(x => x.idParo).filter(Boolean)
      };
      if (this.upd.aplicarMotivo) payload.idParoTipo = this.upd.idParoTipo ?? null;
      if (this.upd.aplicarSubmotivo) payload.idSubParoTipo = this.upd.idSubParoTipo ?? null;
      if (this.upd.aplicarHoraInicio) payload.horaInicio = this.upd.horaInicio || null;
      if (this.upd.aplicarHoraFin) payload.horaFin = this.upd.horaFin || null;
      if (this.upd.aplicarDescripcion) payload.descripcion = (this.upd.descripcion || '').trim();

      this.loadingActualizar = true;
      try {
        await axios.put('/ParoProgramado/ActualizarMasivo', payload);
        this.cerrarMasivo();
        this.filtrarEventosPorSemana?.();
      } catch (err) {
        console.error('Actualizar masivo error:', err.response?.data || err.message);
      } finally {
        this.loadingActualizar = false;
      }
    },
    limpiarBusqueda() {
      this.filtro = { IdArea: null, IdMaquina: null, IdParoTipo: null, IdSubParoTipo: null, desde: null, hasta: null };
      this.seleccionMasiva = [];
    },

    // === Helpers de comparación ===
    _eq(a, b) { try { return JSON.stringify(a) === JSON.stringify(b); } catch { return a === b; } },
    _commonVal(rows, key) {
      const vals = rows.map(r => r[key]).filter(v => v !== undefined && v !== null);
      if (!vals.length) return null;
      return vals.every(v => this._eq(v, vals[0])) ? vals[0] : null;
    },
    _commonArrayEqual(arrs) {
      const norm = a => (a || []).map(x => Number(x)).sort((x, y) => x - y);
      if (!arrs.length) return [];
      const first = JSON.stringify(norm(arrs[0]));
      return arrs.every(a => JSON.stringify(norm(a)) === first) ? JSON.parse(first) : [];
    },

    // === Precargar comunes en el MISMO 'form' al entrar a Paso 2 ===
    initPaso2() {
      const sel = this.seleccionMasiva || [];

      // Extrae IDs desde los detalles de la tabla (tú ya muestras los nombres)
      const areaIDs = sel.map(x => (x.areasDetalle || []).map(a => a.id ?? a.value ?? a.IdArea));
      const maqIDs = sel.map(x => (x.maquinasDetalle || []).map(m => m.id ?? m.value ?? m.IdMaquinaIns));

      const comunes = {
        motivo: this._commonVal(sel, 'idParoTipo'),
        submotivo: this._commonVal(sel, 'idSubParoTipo'),
        areas: this._commonArrayEqual(areaIDs),
        maquinas: this._commonArrayEqual(maqIDs),
        desde: this._commonVal(sel, 'desde') ? this.dateOnly(this._commonVal(sel, 'desde')) : null,
        hasta: this._commonVal(sel, 'hasta') ? this.dateOnly(this._commonVal(sel, 'hasta')) : null,
        horaInicio: this._commonVal(sel, 'horaInicio'),
        horaFin: this._commonVal(sel, 'horaFin'),
        descripcion: this._commonVal(sel, 'descripcion'),
        dias: [] // opcional, no se infiere automáticamente
      };

      // Guarda snapshot y rellena el MISMO 'form' (el del modal crear/editar)
      this.originalMasivo = JSON.parse(JSON.stringify({ ...this.form, ...comunes }));
      this.form = { ...this.form, ...comunes };
    },

    // === Armar payload solo con campos realmente cambiados ===
    _buildDirtyMasivo() {
      const keys = ['motivo', 'submotivo', 'areas', 'maquinas', 'desde', 'hasta', 'horaInicio', 'horaFin', 'descripcion', 'dias'];
      const dirty = {};
      for (const k of keys) {
        const v = this.form[k];
        const o = this.originalMasivo ? this.originalMasivo[k] : undefined;
        const empty = (v === null || v === '' || (Array.isArray(v) && v.length === 0));
        if (!empty && !this._eq(v, o)) dirty[k] = v;
      }
      // Mapea a lo que espera la API
      if (dirty.motivo !== undefined) { dirty.idParoTipo = dirty.motivo; delete dirty.motivo; }
      if (dirty.submotivo !== undefined) { dirty.idSubParoTipo = dirty.submotivo; delete dirty.submotivo; }
      return dirty;
    },

    // === Submit masivo (NO hacemos validate en este form) ===
    async aplicarMasivo() {
      this.loadingActualizar = true;
      try {
        const cambios = this._buildDirtyMasivo();
        if (Object.keys(cambios).length === 0) { this.loadingActualizar = false; return; }

        const ids = (this.seleccionMasiva || []).map(x => x.idParoProgramado);
        const payload = { idsParoProgramado: ids, ...cambios };

        // Opcional: inspeccionar payload
        // console.log('PUT /parosprogramados/ActualizarMasivo', payload);

        await axios.put('/parosprogramados/ActualizarMasivo', payload);

        // Reset mínimo post-actualización
        this.stepMasivo = 1;
        this.seleccionMasiva = [];
        this.buscarParos();
      } finally {
        this.loadingActualizar = false;
      }
    },

    abrirEliminar(record) {
      this.eliminarError = '';
      this.dialogEliminar = true;

      const r = record || {};
      const toYMD = s => (s || '').slice(0, 10);

      this.$nextTick(() => {
        this.eliminarForm = {
          idParoProgramado: r.idParoProgramado ?? null,
          desde: toYMD(r.desde),
          hasta: toYMD(r.hasta),
          areas: (r.areas || r.areasDetalle || []).map(x => x.id ?? x),
          maquinas: (r.maquinas || r.maquinasDetalle || []).map(x => x.id ?? x),
          motivo: ''
        };
      });
    },

    cerrarEliminar() {
      if (!this.eliminarLoading) this.dialogEliminar = false;
    },

    buildEliminarPayload() {
      return {
        idParoProgramado: this.eliminarForm.idParoProgramado,
        desde: this.eliminarForm.desde,
        hasta: this.eliminarForm.hasta,
        areas: this.eliminarForm.areas,
        maquinas: this.eliminarForm.maquinas,
        motivo: this.eliminarForm.motivo || null,
        activo: false // eliminación lógica
      };
    },

    abrirEliminar() {
      this.eliminarError = '';
      this.dialogEliminar = true;
    },

    async eliminarActual() {
      const id = this.form?.idParoProgramado ?? null;
      if (!id) { this.eliminarError = 'Falta IdParoProgramado.'; return; }

      this.eliminarLoading = true;
      this.eliminarError = '';
      try {
        await axios.put(`/ParoProgramado/Desactivar/${id}`);
        this.dialogEliminar = false;
        this.dialog = false; // cierra el diálogo principal
        this.filtrarEventosPorSemana && this.filtrarEventosPorSemana();
      } catch (e) {
        this.eliminarError = e?.response?.data || e?.message || 'Error eliminando el actual.';
      } finally {
        this.eliminarLoading = false;
      }
    },

    async eliminarTodos() {
      const idProg = this.form?.idProgramado ?? null;
      if (!idProg) { this.eliminarError = 'Falta IdProgramado.'; return; }

      this.eliminarLoading = true;
      this.eliminarError = '';
      try {
        await axios.put(`/ParoProgramado/DesactivarParos/${idProg}`);
        this.dialogEliminar = false;
        this.dialog = false; // cierra el diálogo principal
        this.filtrarEventosPorSemana && this.filtrarEventosPorSemana();
      } catch (e) {
        this.eliminarError = e?.response?.data || e?.message || 'Error eliminando todos.';
      } finally {
        this.eliminarLoading = false;
      }
    },
  },
  watch: {
    // 'form.desde'() { this.recalcularDiasInvolucrados(); },
    // 'form.hasta'() { this.recalcularDiasInvolucrados(); },
    'form.motivo'(nuevo) {
      this.subcausasFiltradas = Array.isArray(this.subcausas)
        ? this.subcausas.filter(s => (s.idParoTipo ?? s.IdParoTipo) === nuevo)
        : [];
      this.form.submotivo = null;
    },
    stepMasivo(n) { if (n === 2) this.initPaso2(); }
  },
  computed: {
    diasSemanaEnRango() {
      const { desde, hasta } = this.form;
      if (!desde || !hasta) return [];
      const set = new Set();
      let cur = new Date(`${desde}T00:00:00`);
      const fin = new Date(`${hasta}T00:00:00`);
      while (cur <= fin) {
        set.add(cur.getDay()); // 0=Dom,1=Lun,...6=Sáb
        cur.setDate(cur.getDate() + 1);
      }
      return this.diasDisponibles.filter(d => set.has(d.value)); // usa tus {label,value}
    },
    recalcularDiasInvolucrados() {
      const { desde, hasta } = this.form;
      const out = [];
      if (!desde || !hasta) {
        this.diasInvolucrados = [];
        return;
      }
      let cur = new Date(`${desde}T00:00:00`);
      const fin = new Date(`${hasta}T00:00:00`);
      while (cur <= fin) {
        out.push(this.toYMD(cur));
        cur.setDate(cur.getDate() + 1);
      }
      this.diasInvolucrados = out;
    },
    nombreMes() {
      const mes = this.meses.find(m => m.value === this.mesSeleccionado)
      return mes ? mes.text : ''
    },

    esFinAntesQueInicio() {
      if (!this.form.horaInicio || !this.form.horaFin) return false;
      return this.form.horaFin < this.form.horaInicio; // compara "HH:mm"
    },
    // puedeGuardar() {
    //   return this.form.dias.length > 0
    //     && this.form.horaInicio
    //     && this.form.horaFin;
    // },
    // 01/01 del año actual
    startOfYear() {
      const y = new Date().getFullYear();
      return `${y}-01-01`;
    },
    // 31/12 del año actual
    endOfYear() {
      const y = new Date().getFullYear();
      return `${y}-12-31`;
    },

    // diasInvolucrados() {
    //   const { desde, hasta } = this.form;
    //   if (!desde || !hasta) return [];

    //   // 1) Normalizar fechas a medianoche local para evitar zona horaria
    //   const start = new Date(desde + 'T00:00:00');
    //   const end = new Date(hasta + 'T00:00:00');

    //   // 2) Iterar día a día, incluyendo el último
    //   const dias = [];
    //   for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    //     const nombre = d.toLocaleDateString('es-ES', { weekday: 'long' });
    //     dias.push(nombre.charAt(0).toUpperCase() + nombre.slice(1));
    //   }

    //   // 3) Eliminar duplicados (por si acaso) y devolver
    //   return Array.from(new Set(dias));
    // },
    diasInvolucrados() {
      const { desde, hasta } = this.form;
      if (!desde || !hasta) return [];
      const out = [];
      let cur = new Date(`${desde}T00:00:00`);
      const fin = new Date(`${hasta}T00:00:00`);
      while (cur <= fin) {
        out.push(this.toLocalYMD(cur));
        cur.setDate(cur.getDate() + 1);
      }
      return out;
    },
    textoCausa() {
      if (this.form?.motivoNombre) return this.form.motivoNombre;
      const id = this.form?.motivo ?? this.form?.idParoTipo;
      if (id == null) return '—';
      const item = (this.causas || []).find(x => (x.idParoTipo ?? x.id) === id);
      return item?.nombre || '—';
    },
    textoSubcausa() {
      if (this.form?.submotivoNombre) return this.form.submotivoNombre;
      const id = this.form?.submotivo ?? this.form?.idSubParoTipo;
      if (id == null) return '—';
      const pools = [
        ...(this.subcausasFiltradas || []),
        ...(this.subcausas || [])
      ];
      const it = pools.find(x => (x.idSubParoTipo ?? x.id) === id);
      return it?.nombreSubParoTipo || it?.nombre || '—';
    },

    textoAreas() {
      // 1) Si viene areasDetalle, usar sus nombres
      const det = this.form?.areasDetalle;
      if (Array.isArray(det) && det.length) {
        const nombres = det.map(x => x?.nombre).filter(Boolean);
        return nombres.length ? nombres.join(', ') : '—';
      }
      // 2) Mapear IDs -> nombre con catálogo
      const ids = Array.isArray(this.form?.areas) ? this.form.areas : [];
      const key = v => String(v);
      const mapa = new Map((this.areas || []).map(a => [key(a.id ?? a.value), a.nombre ?? a.text]));
      const nombres = ids.map(id => mapa.get(key(id))).filter(Boolean);
      return nombres.length ? nombres.join(', ') : '—';
    },

    textoMaquinas() {
      // 1) Si viene maquinasDetalle, usar sus nombres
      const det = this.form?.maquinasDetalle;
      if (Array.isArray(det) && det.length) {
        const nombres = det.map(x => x?.nombre).filter(Boolean);
        return nombres.length ? nombres.join(', ') : '—';
      }
      // 2) Mapear IDs -> nombre con catálogo
      const ids = Array.isArray(this.form?.maquinas) ? this.form.maquinas : [];
      const key = v => String(v);
      const mapa = new Map((this.maquinas || []).map(m => [key(m.id ?? m.value), m.nombre ?? m.text]));
      const nombres = ids.map(id => mapa.get(key(id))).filter(Boolean);
      return nombres.length ? nombres.join(', ') : '—';
    },



    // subcausasFiltradas() {
    //   if (!this.form.motivo) return []
    //   return this.subcausas.filter(item => item.idParoTipo === this.form.motivo);
    // }
  },
};
</script>

<style scoped>
/* ------------------------------------------------ */
/* 1) Oculta completamente la cabecera nativa week   */
/* ------------------------------------------------ */
.v-calendar-weekly__head {
  display: none !important;
}

/* ------------------------------------------------ */
/* 2) Header personalizado: flex + padding-left       */
/* ------------------------------------------------ */
.calendar-header {
  display: flex;
  align-items: center;
  /* Ajusta este valor al ancho de la columna de horas */
  padding-left: 60px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 8px;
  padding-top: 8px;
  padding-bottom: 8px;
}

/* Cada “celda” de día */
.day-header-cell {
  flex: 1;
  text-align: center;
}

/* ------------------------------------------------ */
/* 3) Burbuja de número de día                       */
/* ------------------------------------------------ */
.day-bubble {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #e0e0e0;
  color: #282828;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 17px;
}

/* La burbuja del día actual en azul */
.day-bubble.today {
  background: #1976d2;
  color: #fff;
}

/* Nombre abreviado de cada día */
.weekday-label {
  font-size: 15px;
  color: #777;
  margin-top: 4px;
}

.my-event {
  color: #fff;
  border-radius: 10px;
  padding: 6px 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .15);

  /* Nuevo layout */
  display: grid;
  grid-template-rows: auto 1fr;
  /* título arriba, submotivo ocupa el resto */
  gap: 4px;
  height: 100%;
  overflow: hidden;
  line-height: 1.2;
}

/* Título: centrado, 2 líneas máx, que QUEPA (wrap) */
.my-event__title {
  font-weight: 700;
  font-size: 12px;
  text-align: center;
  white-space: normal;
  /* <- quita el nowrap */
  word-break: break-word;
  overflow-wrap: anywhere;
  display: -webkit-box;
  /*-webkit-line-clamp: 2;        /* máx 2 líneas */
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Submotivo: hasta 4 líneas, rellena el bloque */
.my-event__subtitle {
  font-size: 11px;
  opacity: .95;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  display: -webkit-box;
  /*-webkit-line-clamp: 4;        /* máx 4 líneas (ajústalo si tu bloque es más alto) */
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Tooltip, igual */
.tooltip-content {
  max-width: 360px;
  padding: 6px 8px;
}

.tooltip-row {
  margin: 2px 0;
  font-size: 12px;
}
</style>
